import { FETCH_ERROR } from "@boark/shared/consts/fetch-error";
import { ProcurementCommentCreate } from "@boark/shared/types/procurement-comment-create";
import { ProcurementCommentUpdate } from "@boark/shared/types/procurement-comment-update";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createProcurementComment,
  deleteProcurementComment,
  getProcurementComments,
  updateProcurementComment,
} from "../services/procurement-comment";
import { ProcurementCommentWithCreatedBy } from "@boark/shared/types/procurement-comment-with-created-by";

export function procurementCommentsQueryKey(procurementId: string) {
  return ["procurement-comments", procurementId] as const;
}

export function useGetProcurementComments(procurementId: string) {
  return useQuery({
    queryKey: procurementCommentsQueryKey(procurementId),
    queryFn: () => getProcurementComments(procurementId),
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });
}

export function useCreateProcurementComment(procurementId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (comment: ProcurementCommentCreate) => {
      const created = await createProcurementComment(procurementId, comment);
      queryClient.setQueryData(
        procurementCommentsQueryKey(procurementId),
        (existing: ProcurementCommentWithCreatedBy[]) => [created, ...(existing ?? [])],
      );
      return created;
    },
  });
}

export function useUpdateProcurementComment(procurementId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      comment,
    }: {
      commentId: string;
      comment: ProcurementCommentUpdate;
    }) => {
      const updated = await updateProcurementComment(
        procurementId,
        commentId,
        comment,
      );
      queryClient.setQueryData(
        procurementCommentsQueryKey(procurementId),
        (existing) =>
          existing?.map((item) =>
            item.id === updated.id ? updated : item,
          ) ?? [updated],
      );
      return updated;
    },
  });
}

export function useDeleteProcurementComment(procurementId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      await deleteProcurementComment(procurementId, commentId);
      queryClient.setQueryData(
        procurementCommentsQueryKey(procurementId),
        (existing) => existing?.filter((item) => item.id !== commentId) ?? [],
      );
    },
  });
}
